/**
 * Platform maintenance, shared (#3573, feature #3561).
 *
 * One fetch for the whole app. The banner used to ask `GET /maintenance` on its
 * own; now the action gates need the same answer, and N components polling the
 * same endpoint is N components that can disagree about whether we are in a
 * maintenance window.
 *
 * Two values, because they answer two different questions:
 *
 *   `state`      — what the window IS. The banner renders this.
 *   `isBlocking` — whether THIS session should be stopped from changing things.
 *
 * They differ for exactly one group. Platform admins are the people running the
 * maintenance: they are not blocked, and they are not shown the notice either —
 * it is addressed to customers, and an admin console papered with a customer
 * banner is noise on the screen of the person fixing the thing.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getMaintenancePublic } from "@service/admin";
import { MaintenanceState } from "@type/admin";
import { useUserProfileContext } from "@context/user_profile";
import { installMaintenanceGuard, setMaintenanceBlocking } from "@util/maintenance-guard";

/** The roles that run the platform, and therefore run the maintenance. */
export function isPlatformAdmin(role?: string | null): boolean {
    return role === "admin" || role === "super_admin";
}

/**
 * Re-read on an interval so turning maintenance OFF releases the app without
 * anyone reloading. A window is measured in hours and the payload is four
 * fields, so a minute is frequent enough and cheap enough.
 */
const POLL_MS = 60_000;

interface MaintenanceContextValue {
    /** The window itself, or null while it is still being read. */
    state: MaintenanceState | null;
    /** Maintenance is on AND this session is one it applies to. */
    isBlocking: boolean;
}

const MaintenanceContext = createContext<MaintenanceContextValue>({
    state: null,
    isBlocking: false,
});

export function MaintenanceProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<MaintenanceState | null>(null);
    const [, userInfo] = useUserProfileContext();

    // Installed once, here rather than at module load, so importing the guard
    // in a test does not attach an interceptor to the shared axios instance.
    useEffect(installMaintenanceGuard, []);

    useEffect(() => {
        let cancelled = false;
        let timer: number | undefined;

        const read = () => {
            getMaintenancePublic()
                .then((next) => { if (!cancelled) setState(next); })
                // A failed read is not an outage. `getMaintenancePublic` already
                // resolves to an inactive state rather than throwing; this is the
                // belt for anything it does not catch. Failing the other way would
                // disable the whole product on a blip.
                .catch(() => { if (!cancelled) setState(null); })
                .finally(() => { if (!cancelled) timer = window.setTimeout(read, POLL_MS); });
        };

        read();
        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, []);

    const isBlocking = !!state?.is_active && !isPlatformAdmin(userInfo?.role);

    // Keep the request guard in step. It runs outside React — an axios
    // interceptor has no hooks — so the flag is pushed to it rather than read.
    useEffect(() => {
        setMaintenanceBlocking(isBlocking);
        return () => setMaintenanceBlocking(false);
    }, [isBlocking]);

    return (
        <MaintenanceContext.Provider value={{ state, isBlocking }}>
            {children}
        </MaintenanceContext.Provider>
    );
}

export function useMaintenance(): MaintenanceContextValue {
    return useContext(MaintenanceContext);
}

/**
 * What a disabled control says, or undefined when nothing is blocked.
 *
 * Deliberately does NOT promise that anything is locked. There is no
 * server-side enforcement (ourteam-backend#4645, closed not-planned), so
 * "read-only" or "frozen" would be a claim the system does not keep. "Paused"
 * is what is true: we have stopped offering the action, not stopped accepting
 * it.
 */
export const MAINTENANCE_TOOLTIP = "Changes are paused while scheduled maintenance is in progress.";

/**
 * The gate a component applies to a group of actions (#3573).
 *
 * Most screens that block actions already own a wrapper element for it —
 * trial-expired got there first, and it dims a whole action row rather than
 * threading `disabled` through every nested DropdownMenuTrigger. Maintenance
 * wants the same treatment on the same wrappers, so this returns pieces to mix
 * into an existing element rather than a component that would wrap one more
 * `<div>` around layouts that are load-bearing flex rows.
 *
 * `inert` rather than `pointer-events-none` alone: pointer-events stops the
 * mouse and nothing else — a control keeps its place in the tab order and
 * still fires on Enter. `inert` (React 19; baseline in browsers since 2023)
 * takes the subtree out of the tab order and the accessibility tree too, which
 * is the claim actually being made: these are not available right now.
 *
 * No `title` here. A tooltip cannot be read off an inert subtree, and the
 * banner above the page already says what is happening and until when — which
 * is the job the banner exists for.
 */
export function useMaintenanceGate(): {
    /** Maintenance applies to this session. */
    blocked: boolean;
    /** Tooltip text for a single control that can carry one, else undefined. */
    tooltip: string | undefined;
    /** Spread onto the element wrapping a group of actions. */
    groupProps: { inert?: boolean; "aria-disabled"?: boolean };
    /** Append to that element's className. */
    groupClass: string;
} {
    const { isBlocking } = useMaintenance();
    return {
        blocked: isBlocking,
        tooltip: isBlocking ? MAINTENANCE_TOOLTIP : undefined,
        groupProps: isBlocking ? { inert: true, "aria-disabled": true } : {},
        groupClass: isBlocking ? "opacity-50" : "",
    };
}
